import React from "react";
import { Box, Typography } from "@mui/material";

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      minHeight: 120,
      borderRadius: "8px",
      bgcolor: "#f8fafc",
      border: "1px dashed #e2e8f0",
      px: 2,
    }}
  >
    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", textAlign: "center" }}>
      No data available
    </Typography>
  </Box>
);

/**
 * Column-header row + colored-dot ranked rows with a trailing bar+percent,
 * shared by Top Tests and Patients by Department. Callers normalize their
 * own data into `rows: [{ name, color, count, pct }]`.
 */
const RankedListCard = ({ nameLabel = "Name", countLabel = "Count", rows = [], showBar = false }) => {
  if (!rows.length) {
    return <EmptyState />;
  }

  return (
    <Box sx={{ width: "100%", height: "100%", minWidth: 0, display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 52px 44px",
          columnGap: 1.25,
          pb: 0.4,
          mb: 0.6,
          borderBottom: "1px solid #e8edf3",
        }}
      >
        <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, color: "#94a3b8" }}>
          {nameLabel}
        </Typography>
        <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, color: "#94a3b8", textAlign: "right" }}>
          {countLabel}
        </Typography>
        <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, color: "#94a3b8", textAlign: "right" }}>
          Share
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.55 }}>
        {rows.map((row, index) => (
          <Box key={`${row.name}-${index}`} sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 52px 44px", columnGap: 1.25, alignItems: "center" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: row.color, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    color: "#334155",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={row.name}
                >
                  {row.name}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#0f172a", textAlign: "right" }}>
                {row.count}
              </Typography>
              <Typography sx={{ fontSize: "0.66rem", fontWeight: 600, color: "#94a3b8", textAlign: "right" }}>
                {row.pct.toFixed(0)}%
              </Typography>
            </Box>
            {showBar ? (
              <Box sx={{ width: "100%", height: 3, bgcolor: "#f1f5f9", borderRadius: "2px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${Math.min(row.pct, 100)}%`, bgcolor: row.color, borderRadius: "2px" }} />
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default React.memo(RankedListCard);
